'use client'

import clsx from 'clsx'
import { FC, useMemo } from 'react'
import { DateTimeZoneFragment } from '../../../lib/graphql/generated/types'
import { dayjsFrom } from '../../../lib/utils'
import styles from './formattedDate.module.css'

export enum DateFormat {
    Long = 'ddd, D. MMMM YYYY',
    Medium = 'D. MMM YYYY',
    Short = 'D.M.YYYY',
    Time = 'HH:mm',
    TimeTz = 'HH:mm z',
}

interface Props {
    className?: string
    endDate?: any
    format?: DateFormat.Short | DateFormat.Medium | DateFormat.Long
    startDate?: any
    timezone?: DateTimeZoneFragment
    withTime?: boolean
}

const FormattedDate: FC<Props> = props => {
    const startDate = useMemo(
        () => dayjsFrom(props.startDate, props.timezone?.name),
        [props.startDate, props.timezone?.name]
    )

    const startTime = useMemo(
        () =>
            startDate &&
            startDate.format(
                props.timezone?.default === false
                    ? DateFormat.TimeTz
                    : DateFormat.Time
            ),
        [props.timezone?.default, startDate]
    )

    const startTimeWithoutTz = useMemo(
        () => startDate && startDate.format(DateFormat.Time),
        [startDate]
    )

    const endDate = useMemo(
        () => dayjsFrom(props.endDate, props.timezone?.name),
        [props.endDate, props.timezone?.name]
    )

    const endTime = useMemo(
        () =>
            endDate &&
            endDate.format(
                props.timezone?.default === false
                    ? DateFormat.TimeTz
                    : DateFormat.Time
            ),
        [endDate, props.timezone?.default]
    )

    if (!startDate) {
        return null
    }

    const format = props.format ?? DateFormat.Short

    if (!endDate || endDate.diff(startDate, 'minute') === 0) {
        return (
            <span className={clsx(props.className)}>
                {startDate.format(format)}
                {props.withTime && <>, {startTime}</>}
            </span>
        )
    }

    // Render as one day event as long as the end time is before 5am
    if (
        startDate.format(DateFormat.Short) ===
            endDate.format(DateFormat.Short) ||
        (endDate.diff(startDate, 'day') === 0 && endDate.hour() < 5)
    ) {
        return (
            <span className={clsx(props.className)}>
                {startDate.format(format)}
                {props.withTime && (
                    <>
                        , {startTimeWithoutTz} &ndash; {endTime}
                    </>
                )}
            </span>
        )
    }

    return (
        <span className={props.className}>
            <span className={styles.noWrap}>
                {startDate.format(format)}
                {props.withTime && <>, {startTimeWithoutTz}</>} &ndash;
            </span>{' '}
            <span className={styles.noWrap}>
                {endDate.format(format)}
                {props.withTime && <>, {endTime}</>}
            </span>
        </span>
    )
}

export default FormattedDate
